import React from 'react';
import { FormattedMessage } from 'react-intl';
import MoreInfoModal from '@alltrails/shared/components/MoreInfoModal';
import useModalPortalElement from 'hooks/useModalPortalElement';
import {
  Filter,
  FilterCheckbox,
  FilterConfig,
  FilterConfigSlider,
  FilterConfigStarRating,
  FilterConfigToggle,
  FilterConfigToggleKey,
  FilterInput,
  FilterRadio,
  FilterType
} from 'types/Search';
import { StringConstantsMixin } from '../../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import CheckboxGroup from '../CheckboxGroup';
import RadioGroup from '../RadioGroup';
import RangeGroup from '../RangeGroup';
import StarRatingGroup from '../StarRatingGroup';
import * as styles from './styles/styles.module.scss';

export type Props = {
  filter: Filter;
  onChange: (changes: FilterConfig) => void;
};

const filterMoreInfo: { [key: string]: { [key: string]: { title: React.ReactNode; content: React.ReactNode } } } = {
  [FilterType.DIFFICULTY]: {
    ...[
      StringConstantsMixin.strings.DIFFICULTY.NORMAL.EASY,
      StringConstantsMixin.strings.DIFFICULTY.NORMAL.MODERATE,
      StringConstantsMixin.strings.DIFFICULTY.NORMAL.HARD
    ].reduce(
      (memo, key) => ({
        ...memo,
        [key]: {
          title: <FormattedMessage defaultMessage="Difficulty levels" />,
          content: (
            <FormattedMessage
              values={{ p: (...chunks: React.ReactNode[]) => <p>{chunks}</p> }}
              defaultMessage="<p>Difficulty levels are determined by distance, elevation gain, and terrain.</p><p>For example, an easy trail would not have much elevation gain or would be a shorter distance. Easy trails can also be long flat trails such as rails-trails. A hard trail could have a significant amount of elevation gain and be long in distance or have some sort of terrain component that makes it challenging. Moderate is somewhere in between those.</p><p>The user reviews will also provide insight into trail difficulty levels as well as conditions which can change depending on season and can affect difficulty. We always recommend reading user reviews when determining a trails difficulty or using additional filters to fit your needs.</p>"
            />
          )
        }
      }),
      {}
    )
  },
  [FilterType.SUITABILITY]: {
    [StringConstantsMixin.strings.FILTERS.ACCESS.ADA]: {
      title: <FormattedMessage defaultMessage="Wheelchair friendly" />,
      content: (
        <FormattedMessage
          values={{ p: (...chunks: React.ReactNode[]) => <p>{chunks}</p> }}
          defaultMessage="<p>This rating is based on trail width, grade, cross-slopes, obstacles, surface firmness and type. Wheelchair friendly does not mean a trail is certified to be compliant with all country-specific accessibility legal standards.</p><p>Note that trail conditions can vary depending on time, weather and other events. Consider user skill and equipment type when choosing your trail.</p>"
        />
      )
    }
  }
};

const FilterConfigName = ({ filter, configKey }: { filter: FilterRadio | FilterCheckbox; configKey: FilterConfigToggleKey }) => {
  const optionDisplayName = filter.config[configKey]?.name;
  const moreInfoEntry = filterMoreInfo[filter.key]?.[optionDisplayName];

  const modalElement = useModalPortalElement();

  return (
    <span className={styles.option}>
      {optionDisplayName}
      {moreInfoEntry && (
        <MoreInfoModal className={styles.moreInfoButton} title={moreInfoEntry.title} modalElement={modalElement}>
          {moreInfoEntry.content}
        </MoreInfoModal>
      )}
    </span>
  );
};

const normalizeFilterConfig = (filter: FilterCheckbox | FilterRadio): FilterConfigToggle =>
  Object.keys(filter.config).reduce(
    (config, nextKey: FilterConfigToggleKey) => ({
      ...config,
      [nextKey]: {
        ...filter.config[nextKey],
        name: <FilterConfigName filter={filter} configKey={nextKey} />
      }
    }),
    {} as FilterConfigToggle
  );

const FilterInputFactory = ({ filter, onChange }: Props) => {
  switch (filter.type) {
    case FilterInput.CHECKBOX:
      return (
        <CheckboxGroup
          className={filter.extraProps?.className}
          config={normalizeFilterConfig(filter)}
          filterName={filter.key}
          handler={onChange}
          useColumns={filter.extraProps?.useColumns}
          variant={filter.extraProps?.variant}
        />
      );
    case FilterInput.RADIO:
      return (
        <RadioGroup
          className={filter.extraProps?.className}
          config={normalizeFilterConfig(filter)}
          filterName={filter.key}
          handler={onChange}
          useColumns={filter.extraProps?.useColumns}
          variant={filter.extraProps?.variant}
        />
      );
    case FilterInput.SLIDER:
      return <RangeGroup config={filter.config as FilterConfigSlider} handler={onChange} />;
    case FilterInput.STAR_RATING:
      return <StarRatingGroup config={filter.config as FilterConfigStarRating} handler={onChange} />;
    default:
      // eslint-disable-next-line no-console
      console.warn(`FilterInputFactory unexpected filter type: ${JSON.stringify(filter)}`);
      return null;
  }
};

export default FilterInputFactory;
